import { ModalBody as ChakraModalBody } from '@chakra-ui/react'
import styled from '@emotion/styled'
import * as React from 'react'

import { noScrollbarStyles } from '~ui/components/layout/NoScrollbarBox'
import { getSpacingValue, TSpace } from '~ui/styleGuide/spaces'

import { useModalSettingsCtx } from '../context/ModalSettingsProvider'

type TProps = RequiredChildren & {
  /**
   * unique id to access the modal body from anywhere
   */
  id?: PropsOf<typeof ChakraModalBody>['id']
  /**
   * space inside the modal’s body,
   * overrides the padding that the body takes from the Modal itself
   * can be single space key,
   * object with `horizontal` and `vertical` attributes
   * or separate values for each side `top`, `right`, `bottom`, `left
   */
  padding?: TSpace
  /**
   * overflow of the modal body,
   * @default 'auto'
   */
  overflow?: PropsOf<typeof ChakraModalBody>['overflow']
}

// Chakra modal body with removed scrollbar
const StyledChakraModalBody = styled(ChakraModalBody)`
  ${noScrollbarStyles}
`

/**
 * Component for rendering the main section of the Modal.
 * Wrapping the Chakra’s ModalBody.
 * Does sync padding with the rest of the Modal if not overridden.
 */
export const ModalBody: React.FC<TProps> = ({ id, padding, overflow = `auto`, children }) => {
  const { bodyPadding } = useModalSettingsCtx()

  const paddingValue = padding ? getSpacingValue(padding) : bodyPadding

  return (
    <StyledChakraModalBody id={id} overflow={overflow} padding={paddingValue} flexGrow={1}>
      {children}
    </StyledChakraModalBody>
  )
}
